import axios from 'axios';
import { ApiResponse, ClientData, GeneralStats, UpdateResult, SyncResult } from '../types';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : 'http://localhost:4444/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 2 minutos para actualizaciones grandes
});

export const clientsApi = {
  // Obtener todos los clientes para un mes específico (vista mensual)
  getClients: async (year: number, month: number): Promise<ApiResponse<ClientData[]>> => {
    const response = await api.get(`/clients?year=${year}&month=${month}`);
    return response.data;
  },

  // Obtener todos los clientes para un mes específico (vista por ciclo)
  getClientsByCycle: async (year: number, month: number): Promise<ApiResponse<ClientData[]>> => {
    const response = await api.get(`/clients/cycle?year=${year}&month=${month}`);
    return response.data;
  },

  // Obtener estadísticas generales
  getStats: async (): Promise<ApiResponse<GeneralStats>> => {
    const response = await api.get('/clients/stats');
    return response.data;
  },

  // Actualizar datos de todos los clientes
  updateAllClients: async (year: number, month: number): Promise<ApiResponse<UpdateResult>> => {
    const response = await api.post('/clients/update', { year, month });
    return response.data;
  },

  // Inicializar clientes desde CSV
  initializeClients: async (): Promise<ApiResponse<{ count: number }>> => {
    const response = await api.post('/clients/initialize');
    return response.data;
  },

  // Sincronizar clientes desde DigitalOcean
  syncFromDigitalOcean: async (): Promise<ApiResponse<SyncResult>> => {
    const response = await api.post('/clients/sync-digitalocean');
    return response.data;
  },

  // Obtener uso diario agregado
  getDailyUsage: async (year: number, month: number): Promise<ApiResponse<any>> => {
    const response = await api.get(`/clients/daily-usage?year=${year}&month=${month}`);
    return response.data;
  },

  // Obtener estadísticas de DigitalOcean
  getDigitalOceanStats: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/clients/digitalocean-stats');
    return response.data;
  },

  // Obtener datos específicos de un cliente
  getClient: async (name: string, year?: number, month?: number): Promise<ApiResponse<any>> => {
    const params = year && month ? `?year=${year}&month=${month}` : '';
    const response = await api.get(`/clients/${encodeURIComponent(name)}${params}`);
    return response.data;
  },

  // Health check
  healthCheck: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/health');
    return response.data;
  },

  // Obtener datos para gráfica de múltiples meses
  getChartData: async (months: number = 6): Promise<ApiResponse<any>> => {
    const response = await api.get(`/clients/chart-data?months=${months}`);
    return response.data;
  },

  // Obtener estado de la última actualización
  getUpdateStatus: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/clients/update-status');
    return response.data;
  },

  // Actualizar comentario de cliente
  updateClientComment: async (clientName: string, comment: string): Promise<ApiResponse<any>> => {
    const response = await api.put(`/clients/${encodeURIComponent(clientName)}/comment`, { comment });
    return response.data;
  },

  // Obtener comentario de cliente
  getClientComment: async (clientName: string): Promise<ApiResponse<any>> => {
    const response = await api.get(`/clients/${encodeURIComponent(clientName)}/comment`);
    return response.data;
  }
};

export default api;
